import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { IVirtualTourProvider } from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import { virtualTourUrlBuilder } from '@app/helpers';
import { useBreakpoint } from 'hooks';
import { Button, Card, Input } from 'components';
import { CreateListingCardProps, VirtualTourFields } from '../types';
import { StyledGrid } from './StyledComponents';

type ValidationError = 'none' | 'invalid' | 'serverError';

const ERROR_DESCRIPTION: Record<ValidationError, string> = {
  none: '',
  invalid: 'Invalid Link',
  serverError: 'Please try again',
};

type VirtualTourProps = CreateListingCardProps<VirtualTourFields>;

const VirtualTour: React.FC<VirtualTourProps> = ({
  virtualTourUrl,
  setListing,
}) => {
  const isMounted = React.useRef(true);
  const { md, lg } = useBreakpoint();
  const [spatula, setSpatula] = useState<string>();
  const [validating, setValidating] = useState(false);
  const [tourUrlInput, setTourUrlInput] = useState('');
  const [validateError, setValidateError] = useState<ValidationError>('none');

  const setVirtualTourUrl = (url: string) => {
    setListing(prev => ({ ...prev, virtualTourUrl: url }));
  };

  useEffect(() => {
    const fetchSpatula = async () => {
      const {
        data: { spatulaDomain },
      } = await axios.get<{ spatulaDomain: string }>('/secrets/spatula_domain');
      if (isMounted.current) {
        setSpatula(spatulaDomain);
      }
    };
    fetchSpatula();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const validateTourUrl = async () => {
    if (validating) return;
    setValidating(true);
    setValidateError('none');
    try {
      const {
        data: { providers },
      } = await axios.get<{
        providers?: {
          identifier: string;
          provider: IVirtualTourProvider;
        }[];
      }>(`${spatula}/parse?url=${encodeURIComponent(tourUrlInput)}`);

      if (providers?.[0]?.identifier) {
        const [{ provider, identifier }] = providers;
        if (isMounted.current) {
          setVirtualTourUrl(
            virtualTourUrlBuilder({
              type: provider,
              identifier,
            }),
          );
          setTourUrlInput('');
        }
        return;
      }
      if (isMounted.current) setValidateError('invalid');
    } catch (err) {
      errorReporter.error(err);
      if (isMounted.current) setValidateError('serverError');
    } finally {
      if (isMounted.current) setValidating(false);
    }
  };

  const removeVirtualTour = () => {
    setVirtualTourUrl('');
    setTourUrlInput('');
  };
  const removeTourButton = <Button onClick={removeVirtualTour}>Remove</Button>;

  return (
    <Card
      type="primary"
      title="Virtual Tour"
      extra={virtualTourUrl ? removeTourButton : null}
      id="virtualTour"
    >
      {virtualTourUrl ? (
        <IFrameGridContainer>
          <IFrameGridItem>
            <iframe
              width="100%"
              height="100%"
              src={virtualTourUrl}
              frameBorder="0"
              allowFullScreen
            />
          </IFrameGridItem>
        </IFrameGridContainer>
      ) : (
        <StyledGrid $columns={3}>
          <StyledWrapper $lg={lg}>
            <Input
              label="Virtual Tour Link"
              placeholder="Paste link"
              value={tourUrlInput}
              onChange={({ target: { value } }) => setTourUrlInput(value)}
              disabled={!spatula}
              error={ERROR_DESCRIPTION[validateError]}
            />
            <Button
              onClick={validateTourUrl}
              disabled={!spatula}
              loading={validating}
            >
              Done
            </Button>
          </StyledWrapper>
        </StyledGrid>
      )}
    </Card>
  );
};

export default React.memo(VirtualTour);

const StyledWrapper = styled.div<{ $lg: boolean }>`
  grid-column-start: 2;
  ${({ $lg }) => {
    if (!$lg) {
      return `
        grid-column-start: 1;
        grid-column-end: 3;
        margin: auto;
      `;
    }
  }}

  display: flex;
  flex-direction: column;
  align-items: center;

  ${Button} {
    margin-top: 15px;
  }
`;

const IFrameGridContainer = styled.div`
  padding-top: 50%;
  overflow: hidden;
  height: 0;
  position: relative;
  margin: auto -24px -24px -24px;
`;

const IFrameGridItem = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;
